/**
 * TanStack Query hook for fetching all loans belonging to the current borrower.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api-client';
import { classifyVestaStage } from '@/lib/lifecycle';

export interface BorrowerLoan {
  loanId: string;
  loanNumber: string;
  /** Display type — 'Cash-Out Refi' is FE-composed from loanPurpose + refinanceType (NP-231). */
  loanType: 'Purchase' | 'Refinance' | 'Cash-Out Refi' | 'HELOC' | string;
  propertyAddress: string;
  /** null until the loan's next wizard save populates the display cache (NP-240, no backfill). */
  loanAmount: number | null;
  status: string;
  statusTone: 'info' | 'success' | 'warning';
  /**
   * NP-70: the milestone the switcher names — Vesta's stage, or for an Encompass loan its
   * Encompass milestone (NP-663 table's milestone column): "Prospects" while the loan is in the
   * Prospects folder, otherwise the milestone as Encompass labels it ("File Started").
   */
  milestone?: string;
  /** NP-240: display names, primary borrower first — includes co-borrowers without PATH accounts. */
  borrowerNames?: string[];
  /** NP-240: ISO timestamp of the last wizard save (API `updatedAt`). */
  lastUpdated?: string;
  /**
   * NP-570 — whether the SIGNED-IN borrower is this loan's primary, as opposed to being on
   * it as a co-borrower. Everything else about a loan is deliberately role-agnostic, so
   * this is the only place the viewer's own role surfaces.
   *
   * Drives the Review page dropping "& Submit" and swapping the submit button for Save &
   * Close (AC-4/AC-5). Rendering only — the submit endpoint enforces the same rule itself
   * (AC-13) and does not trust this.
   *
   * Optional, and read as `!== false` everywhere: the mapper always sets it from the API,
   * so real data always carries it, but absence must mean "assume primary". A missing flag
   * silently removing the Submit button from the person whose application it is would be a
   * worse failure than the one this guards against — and the server-side gate is what
   * actually protects submission.
   */
  isPrimaryBorrower?: boolean;
  /** NP-657 — the LOS that owns the loan. An Encompass loan is read-only in PATH. */
  losType: LosType;
}

export type LosType = 'Vesta' | 'Encompass';

export interface BorrowerLoansResult {
  loans: BorrowerLoan[];
}

/**
 * Raw shape returned by the backend `/borrower/loans` endpoint.
 * The backend uses `purpose` / `estimatedLoanAmount` whereas the
 * frontend UI expects `loanType` / `loanAmount`.
 */
interface ApiBorrowerLoan {
  /** Vesta-direct LoanSummary sends `id`; legacy/mock sends `loanId` */
  id?: string;
  loanId?: string;
  loanNumber?: string | null;
  /** Backend sends `purpose` (e.g. "Purchase", "Refinance") */
  purpose?: string;
  /** Vesta-direct LoanSummary field name */
  loanPurpose?: string | null;
  /** Frontend-compatible alias — present in mock data */
  loanType?: string;
  /** NP-240: display-cache of Vesta subjectProperty.address (one line); null until next wizard save */
  propertyAddress?: string | null;
  /** Backend sends `estimatedLoanAmount` */
  estimatedLoanAmount?: number;
  /** NP-240: wizard loanAmount → Vesta loan.loanAmount; null until next wizard save */
  loanAmount?: number | null;
  status: string;
  /** Vesta-direct LoanSummary milestone (NP-70) — Processing/Underwriting/CTC/Closed */
  currentStage?: string | null;
  /** NP-770: an Encompass loan's milestone as Encompass labels it; null for a Vesta loan. */
  milestone?: string | null;
  /** Legacy/mock only — dropped from the minimal LoanSummary; tone is FE-derived now */
  statusTone?: 'info' | 'success' | 'warning';
  /** NP-240: display names, primary first — falls back to registered name pre-enrichment */
  borrowerNames?: string[];
  /** NP-240: Vesta loan.refinanceCashOutDeterminationType; null for Purchase */
  refinanceType?: 'CashOut' | 'NoOrLimitedCashOut' | string | null;
  /** NP-240: last wizard save — feeds the switcher's relative "last updated" */
  updatedAt?: string;
  /** NP-570: true when the caller is this loan's primary borrower. */
  isPrimaryBorrower?: boolean;
  /** NP-656/NP-657: "vesta" | "encompass"; absent on payloads that predate it, which are all Vesta. */
  losType?: string | null;
}

interface ApiBorrowerLoansResult {
  loans: ApiBorrowerLoan[];
}

/**
 * Normalise backend response fields to the UI-facing `BorrowerLoan` shape.
 * Handles both the legacy/mock shape and the Vesta-direct `{loans,count}`
 * LoanSummary. As of NP-240 (api PR #28) the summary is enriched with
 * propertyAddress / loanAmount / borrowerNames / refinanceType / updatedAt —
 * display-cache columns refreshed on every wizard save (null pre-backfill).
 * statusTone stays FE-derived; loanType is FE-composed (see helpers above).
 */
/**
 * NP-231: compose the display loan type on the FE (agreed with Neo 2026-07-22 —
 * API stays raw). Refinance + CashOut → "Cash-Out Refi". HELOC is deferred
 * until the wizard can originate one (not representable from refinanceType).
 */
function displayLoanType(l: ApiBorrowerLoan): string {
  const purpose = l.loanType ?? l.loanPurpose ?? l.purpose ?? '';
  if (purpose === 'Refinance' && l.refinanceType === 'CashOut') return 'Cash-Out Refi';
  return purpose;
}

/**
 * NP-240/NP-231: statusTone is FE-derived from milestone/status (agreed with
 * Neo 2026-07-22 — presentation stays out of the API). Legacy/mock statusTone
 * still wins when the payload carries one.
 */
function deriveTone(milestone: string | null | undefined, status: string): BorrowerLoan['statusTone'] {
  const s = `${milestone ?? ''} ${status}`.toLowerCase();
  if (/closed|funded|approved|clear to close|\bctc\b/.test(s)) return 'success';
  if (/submitted|processing|underwriting/.test(s)) return 'warning';
  return 'info'; // Prospects / In Progress / drafts
}

function normaliseLoans(raw: ApiBorrowerLoansResult): BorrowerLoansResult {
  return {
    loans: (raw.loans ?? []).map((l) => {
      const isEncompass = l.losType?.toLowerCase() === 'encompass';
      // The API reports an Encompass loan as "Prospects" until it leaves the Prospects folder
      // (EncompassLoanImporter sets the submitted date then), whatever its raw milestone.
      const inProspectsFolder = classifyVestaStage(l.currentStage) === 'prospect';
      return {
        loanId: l.loanId ?? l.id ?? '',
        loanNumber: l.loanNumber ?? '',
        loanType: displayLoanType(l),
        propertyAddress: l.propertyAddress ?? '',
        loanAmount: l.loanAmount ?? l.estimatedLoanAmount ?? null,
        status: l.status,
        statusTone: l.statusTone ?? deriveTone(l.currentStage, l.status),
        // NP-795 (BA correction): the switcher names the Encompass milestone and the Loan journey
        // the PATH stage it maps to, so a Prospects-folder loan reads "Prospects" here, "Lead" there.
        milestone: isEncompass
          ? (inProspectsFolder ? 'Prospects' : l.milestone ?? undefined)
          : l.currentStage ?? undefined,
        borrowerNames: l.borrowerNames ?? [],
        lastUpdated: l.updatedAt,
        // Absent means "we cannot tell", and the safe reading of that is primary: a false
        // here silently removes the Submit button from the person whose application it is.
        // The server-side gate is what actually protects submission (AC-13).
        isPrimaryBorrower: l.isPrimaryBorrower ?? true,
        losType: isEncompass ? 'Encompass' : 'Vesta',
      };
    }),
  };
}

/**
 * The loan list behind the switcher. Exported so a create can invalidate it by
 * the same key the query registers under: with a 5 minute `staleTime`, a newly
 * created loan that does not invalidate this is absent from the dropdown for
 * that long, and `LoanSwitcher`'s `loans.find(...) ?? loans[0]` then displays
 * some other loan as the active one.
 */
export const borrowerLoansKey = ['borrower', 'loans'] as const;

export function useBorrowerLoans() {
  return useQuery({
    queryKey: borrowerLoansKey,
    queryFn: async () => {
      const raw = await apiClient.get<ApiBorrowerLoansResult>('/api/v1/loans');
      const normalised = normaliseLoans(raw);
      // Filter out loans with no meaningful data (empty test records).
      // Vesta-direct minimal LoanSummary has no propertyAddress and may have a
      // null loanNumber pre-submit — keep those rows when they carry a purpose.
      normalised.loans = normalised.loans.filter(loan =>
        loan.loanId && (
          loan.loanNumber ||
          loan.loanType ||
          (loan.propertyAddress && loan.propertyAddress !== 'No property')
        )
      );
      return normalised;
    },
    staleTime: 5 * 60_000,
  });
}
